import { Box, Paper, Typography, useTheme, Slider, Divider, LinearProgress } from "@mui/material";
import { useMemo, useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { FaBuilding, FaRecycle } from "react-icons/fa";
import { LuLayers, LuFootprints, LuSlidersHorizontal, LuAward, LuTableProperties } from "react-icons/lu";
import { useNavigate } from "react-router-dom";
import MetricCard from "../../app_components/MetricCard";
import AdvancedSankey from "../../components/chart/AdvancedSankey";
import { MainContainer } from "../../components/container/MainContainer";
import { useContextProvider } from "../../components/page-context/ContextProvider";
import SegmentedTabs from "../../components/tabs/SegmentedTabs";
import AgGrid from "../../components/grid/AgGrid";
import { defaultColDef, leftAlignColDef, rightAlignColDef } from "../../components/grid/columnDefinitions/columnDefinitions";
import { TableHeaderContainer } from "../../components/container/TableHeaderContainer";
import { TableTitleContainer } from "../../components/container/TableTitleContainer";
import routing from "../../routing";
import { getOrgTokens, ORG_PALETTE } from "../../theme/style/organizationPalette";

const ProductSupplierPortal = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const tok = getOrgTokens(isDark);
  
  const { selectedProducts } = useContextProvider();
  const [activeTab, setActiveTab] = useState("GWP");
  
  // Filtering thresholds
  const [maxGwp, setMaxGwp] = useState(1000000);
  const [minMci, setMinMci] = useState(0);

  // Redirect to dashboard if no product is selected
  useMemo(() => {
    if (!selectedProducts || selectedProducts.length === 0) {
      navigate(routing.sector.products.dashboard);
    }
  }, [selectedProducts, navigate]);

  const activeProducts = selectedProducts || [];

  // Find max GWP value among active products to scale slider appropriately
  const maxGwpValue = useMemo(() => {
    if (activeProducts.length === 0) return 1000;
    return Math.max(...activeProducts.map(p => Number(p.gwp) || 0), 100);
  }, [activeProducts]);

  // Sync maxGwp state when active products are loaded
  useEffect(() => {
    if (maxGwpValue > 0 && maxGwp === 1000000) {
      setMaxGwp(maxGwpValue);
    }
  }, [maxGwpValue]);

  // Overall metrics (calculated on ALL selected products before filtering)
  const metrics = useMemo(() => {
    const totalProducts = activeProducts.length;
    const totalGwp = activeProducts.reduce((sum, p) => sum + (Number(p.gwp) || 0), 0);
    
    const validMciCount = activeProducts.filter(p => p.mci !== undefined && p.mci !== null).length;
    // Database mci is in 0-1 scale, we get average and scale to 0-100 percentage
    const avgMci = validMciCount > 0 
      ? (activeProducts.reduce((sum, p) => sum + (Number(p.mci) || 0), 0) / validMciCount) * 100
      : 0;

    const uniqueSuppliers = new Set(
      activeProducts.map(p => p.supplier && p.supplier.trim() ? p.supplier.trim() : "Unknown Supplier")
    );

    return {
      totalProducts,
      totalGwp,
      avgMci,
      supplierCount: uniqueSuppliers.size
    };
  }, [activeProducts]);

  // Filtered products based on GWP limit AND Circularity threshold
  const filteredProducts = useMemo(() => {
    return activeProducts.filter(p => {
      const gwpVal = Number(p.gwp) || 0;
      const mciVal = Number(p.mci) || 0;
      // Database mci is 0-1, so compare with minMci / 100
      return gwpVal <= maxGwp && mciVal >= minMci / 100;
    });
  }, [activeProducts, maxGwp, minMci]);

  // Supplier scorecard aggregation (based on active/filtered products)
  const supplierScorecard = useMemo(() => {
    const suppliersMap = new Map();

    filteredProducts.forEach((p) => {
      const name = p.supplier && p.supplier.trim() ? p.supplier.trim() : t("Unknown Supplier");
      const gwp = Number(p.gwp) || 0;
      const mci = Number(p.mci) || 0;

      if (!suppliersMap.has(name)) {
        suppliersMap.set(name, {
          name,
          productsCount: 0,
          totalGwp: 0,
          mciSum: 0,
          mciCount: 0
        });
      }

      const entry = suppliersMap.get(name);
      entry.productsCount += 1;
      entry.totalGwp += gwp;
      if (p.mci !== undefined && p.mci !== null) {
        entry.mciSum += mci;
        entry.mciCount += 1;
      }
    });

    const rawScorecard = Array.from(suppliersMap.values()).map((s) => {
      // Scale average MCI to 0-100 percentage
      const avgMci = s.mciCount > 0 ? (s.mciSum / s.mciCount) * 100 : 0;
      return {
        ...s,
        avgMci
      };
    });

    const supplierGwps = rawScorecard.map(s => s.totalGwp);
    const avgGwpLimit = supplierGwps.length > 0 ? (supplierGwps.reduce((a, b) => a + b, 0) / supplierGwps.length) : 0;

    return rawScorecard.map((s) => {
      // Dynamic Eco-Grade logic based on dataset average GWP
      let grade = "C";
      let gradeColor = isDark ? "#fbbf24" : "#d97706"; // Yellow/Orange
      
      const isLowCarbon = s.totalGwp <= avgGwpLimit * 0.8;
      const isHighCarbon = s.totalGwp >= avgGwpLimit * 1.5;

      if (s.avgMci >= 75 && isLowCarbon) {
        grade = "A+";
        gradeColor = isDark ? "#34d399" : "#16a34a"; // Green
      } else if (s.avgMci >= 60) {
        grade = "B";
        gradeColor = isDark ? "#4ade80" : "#22c55e"; // Light Green
      } else if (s.avgMci < 30 || isHighCarbon) {
        grade = "D";
        gradeColor = isDark ? "#f87171" : "#dc2626"; // Red
      }

      return {
        ...s,
        grade,
        gradeColor
      };
    }).sort((a, b) => b.avgMci - a.avgMci || a.totalGwp - b.totalGwp);
  }, [filteredProducts, isDark, t]);

  // Nivo Sankey Data Preparation
  const sankeyData = useMemo(() => {
    const nodesMap = new Map();
    const links = [];

    const isGwpMode = activeTab === "GWP";
    const destId = isGwpMode ? "dest:GWP" : "dest:MCI";
    const destLabel = isGwpMode ? t("Total GWP") : t("Circularity");

    if (filteredProducts.length === 0) {
      return { nodes: [], links: [] };
    }

    // Add final destination node
    nodesMap.set(destId, { 
      id: destId, 
      label: destLabel, 
      color: isDark ? "#c084fc" : "#a78bfa" // Purple
    });

    filteredProducts.forEach((p) => {
      const supplierName = p.supplier && p.supplier.trim() ? p.supplier.trim() : t("Unknown Supplier");
      const supplierId = `supplier:${supplierName}`;
      const productId = `product:${p.id}`;

      // Value calculation
      let val = 0;
      if (isGwpMode) {
        val = Number(p.gwp) || 0;
      } else {
        // Multiplied by 100 for visual weight in Circularity Index mode
        val = (Number(p.mci) || 0) * 100;
      }
      
      const displayVal = Math.max(0.1, val);

      // Dynamic color coding based on values to color individual links
      let linkColor = "rgba(200, 200, 200, 0.3)";
      if (isGwpMode) {
        const pctOfMax = maxGwpValue > 0 ? (Number(p.gwp) || 0) / maxGwpValue : 0;
        if (pctOfMax > 0.75) {
          linkColor = "rgba(239, 68, 68, 0.45)"; // Red (High Carbon)
        } else if (pctOfMax > 0.25) {
          linkColor = "rgba(245, 158, 11, 0.4)";  // Orange (Medium Carbon)
        } else {
          linkColor = "rgba(34, 197, 94, 0.35)";  // Green (Low Carbon)
        }
      } else {
        const mciVal = (Number(p.mci) || 0) * 100;
        if (mciVal >= 75) {
          linkColor = "rgba(16, 185, 129, 0.45)"; // Emerald (High Circularity)
        } else if (mciVal >= 30) {
          linkColor = "rgba(245, 158, 11, 0.4)";  // Orange (Medium Circularity)
        } else {
          linkColor = "rgba(239, 68, 68, 0.4)";   // Red (Low Circularity)
        }
      }

      if (!nodesMap.has(supplierId)) {
        nodesMap.set(supplierId, { 
          id: supplierId, 
          label: supplierName, 
          color: isDark ? "#60a5fa" : "#3b82f6" // Blue
        });
      }

      if (!nodesMap.has(productId)) {
        nodesMap.set(productId, { 
          id: productId, 
          label: p.name || t("Unnamed Product"), 
          color: isDark ? "#34d399" : "#10b981" // Green
        });
      }

      // Link 1: Supplier -> Product
      links.push({
        source: supplierId,
        target: productId,
        value: displayVal,
        color: linkColor
      });

      // Link 2: Product -> Destination
      links.push({
        source: productId,
        target: destId,
        value: displayVal,
        color: linkColor
      });
    });

    return {
      nodes: Array.from(nodesMap.values()),
      links
    };
  }, [filteredProducts, activeTab, isDark, t, maxGwpValue]);

  const tabOptions = [
    { label: t("Global Warming Potential (GWP)"), value: "GWP" },
    { label: t("Circularity Index (MCI)"), value: "MCI" }
  ];

  // Max value for progress bars
  const maxSupplierGwp = Math.max(...supplierScorecard.map(s => s.totalGwp), 1);

  // Dynamic height calculation for Sankey diagram to handle scaling data
  const sankeyHeight = useMemo(() => {
    return Math.max(400, sankeyData.nodes.length * 35);
  }, [sankeyData.nodes]);

  // ag-Grid Column Definitions
  const gridColumnDefs = useMemo(() => [
    { 
      headerName: t("Supplier Name"), 
      field: "name", 
      ...defaultColDef, 
      cellStyle: { ...leftAlignColDef.cellStyle, fontWeight: 600 } 
    },
    { 
      headerName: t("Eco Grade"), 
      field: "grade", 
      ...defaultColDef,
      cellRenderer: (params) => {
        if (!params.value) return "";
        return (
          <span style={{ 
            padding: "3px 10px", 
            borderRadius: "4px", 
            color: "#fff", 
            fontWeight: 700, 
            backgroundColor: params.data.gradeColor,
            fontSize: "11px"
          }}>
            {params.value}
          </span>
        );
      },
      cellStyle: { display: "flex", alignItems: "center", justifyContent: "flex-start" }
    },
    { 
      headerName: t("Products Supplied"), 
      field: "productsCount", 
      ...defaultColDef, 
      ...rightAlignColDef 
    },
    { 
      headerName: t("Total Embodied Carbon (kg CO₂e)"), 
      field: "totalGwp", 
      ...defaultColDef, 
      cellStyle: { ...rightAlignColDef.cellStyle, color: isDark ? "#f87171" : "#dc2626", fontWeight: 600 }, 
      valueFormatter: (params) => params.value ? params.value.toLocaleString() : "0"
    },
    { 
      headerName: t("Average Circularity Index (MCI)"), 
      field: "avgMci", 
      ...defaultColDef, 
      cellStyle: { ...rightAlignColDef.cellStyle, color: isDark ? "#4ade80" : "#16a34a", fontWeight: 600 }, 
      valueFormatter: (params) => params.value !== undefined ? `${params.value.toFixed(1)}%` : "0%"
    }
  ], [isDark, t]);

  return (
    <MainContainer>
      {/* Metrics Section */}
      <Box sx={{ mb: 4 }}>
        <Box className="eng-flex-grid">
          <Box className="eng-metric-item">
            <MetricCard
              title={t("Analyzed Products")}
              value={metrics.totalProducts}
              icon={LuLayers}
              accent="#6366f1"
              bg="rgba(99, 102, 241, 0.12)"
              type="chain"
              delay={0.05}
            />
          </Box>
          <Box className="eng-metric-item">
            <MetricCard
              title={t("Total Embodied Carbon")}
              value={metrics.totalGwp}
              icon={LuFootprints}
              accent="#16a34a"
              bg="rgba(22, 163, 74, 0.12)"
              type="gwp"
              unit="kg CO₂e"
              delay={0.12}
            />
          </Box>
          <Box className="eng-metric-item">
            <MetricCard
              title={t("Average Circularity Index")}
              value={metrics.avgMci}
              icon={FaRecycle}
              accent="#f59e0b"
              bg="rgba(245, 158, 11, 0.12)"
              type="shield"
              unit="%"
              delay={0.19}
            />
          </Box>
          <Box className="eng-metric-item">
            <MetricCard
              title={t("Active Suppliers")}
              value={metrics.supplierCount}
              icon={FaBuilding}
              accent="#38bdf8"
              bg="rgba(14, 165, 233, 0.12)"
              type="chain"
              delay={0.26}
            />
          </Box>
        </Box>
      </Box>

      {/* Full-width Segmented Tabs directly below the metric cards */}
      <Box sx={{ mb: 4, width: "100%" }}>
        <SegmentedTabs 
          options={tabOptions} 
          selected={activeTab} 
          setSelected={setActiveTab} 
          activeColor={tok.bg.tabActive}
        />
      </Box>

      {/* Box layout side-by-side stretching to 100% width with equal 50/50 flex distribution */}
      <Box 
        sx={{ 
          display: "flex", 
          flexDirection: { xs: "column", md: "row" }, 
          gap: 3, 
          mb: 4, 
          width: "100%" 
        }}
      >
        {/* Filters Card */}
        <Paper 
          sx={{ 
            flex: 1,
            p: 3, 
            borderRadius: "12px", 
            border: "1px solid", 
            borderColor: tok.border.card,
            bgcolor: tok.bg.card,
            display: "flex",
            flexDirection: "column",
            textAlign: "left",
            minWidth: 0
          }}
        >
          <Typography variant="subtitle1" sx={{ fontWeight: 600, color: tok.text.chartTitle, display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
            <LuSlidersHorizontal size={18} color={isDark ? "#4ade80" : "#659d33"} />
            {t("Visualization Filters")}
          </Typography>

          <Divider sx={{ mb: 2.5 }} />

          <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5 }}>
            <Box>
              <Typography variant="body2" sx={{ color: tok.text.secondary, mb: 0.5, display: "flex", justifyContent: "space-between" }}>
                <span>{t("Minimum Circularity Index")}</span>
                <strong>&ge; {minMci}%</strong>
              </Typography>
              <Slider
                value={minMci}
                onChange={(e, val) => setMinMci(val)}
                min={0}
                max={100}
                sx={{ color: isDark ? "#4ade80" : "#659d33", py: 0.5 }}
              />
            </Box>

            <Box>
              <Typography variant="body2" sx={{ color: tok.text.secondary, mb: 0.5, display: "flex", justifyContent: "space-between" }}>
                <span>{t("Maximum Carbon Limit (GWP)")}</span>
                <strong>&le; {maxGwp === 1000000 ? Math.ceil(maxGwpValue) : maxGwp} kg CO₂e</strong>
              </Typography>
              <Slider
                value={maxGwp === 1000000 ? maxGwpValue : maxGwp}
                onChange={(e, val) => setMaxGwp(val)}
                min={0}
                max={Math.ceil(maxGwpValue)}
                sx={{ color: isDark ? "#4ade80" : "#659d33", py: 0.5 }}
              />
            </Box>
          </Box>
        </Paper>

        {/* Scorecard Card */}
        <Paper
          sx={{
            flex: 1,
            p: 3,
            borderRadius: ORG_PALETTE.radius.card,
            border: "1px solid",
            borderColor: tok.border.card,
            bgcolor: tok.bg.card,
            display: "flex",
            flexDirection: "column",
            textAlign: "left",
            minWidth: 0
          }}
        >
          <Typography variant="subtitle1" sx={{ fontWeight: 600, color: tok.text.chartTitle, display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
            <LuAward size={18} color={isDark ? "#f59e0b" : "#d97706"} />
            {t("Supplier Scorecard")}
          </Typography>

          <Divider sx={{ mb: 2.5 }} />

          <Box 
            sx={{ 
              flex: 1, 
              display: "flex", 
              flexDirection: "row", 
              gap: 2, 
              overflowX: "auto", 
              pb: 1.5,
              alignItems: "center"
            }}
          >
            {supplierScorecard.map((s, idx) => (
              <Box 
                key={idx} 
                sx={{ 
                  p: 2, 
                  borderRadius: "8px", 
                  border: "1px solid", 
                  borderColor: isDark ? "#27272a" : "#f1f5f9", 
                  bgcolor: isDark ? "rgba(255,255,255,0.01)" : "#fcfdfe",
                  width: 250,
                  minWidth: 250,
                  flexShrink: 0
                }}
              >
                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1 }}>
                  <Typography sx={{ fontWeight: 600, color: tok.text.primary, fontSize: "13px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", maxWidth: "70%" }}>
                    {s.name}
                  </Typography>
                  
                  <Box 
                    sx={{ 
                      px: 1, 
                      py: 0.2, 
                      borderRadius: "4px", 
                      fontSize: "11px", 
                      fontWeight: 700, 
                      color: "#fff", 
                      bgcolor: s.gradeColor
                    }}
                  >
                    {s.grade}
                  </Box>
                </Box>

                <Typography variant="caption" sx={{ color: tok.text.muted, display: "block", mb: 1.5 }}>
                  {s.productsCount} {s.productsCount === 1 ? t("product") : t("products")} supplied
                </Typography>

                <Box sx={{ mb: 1 }}>
                  <Box sx={{ display: "flex", justifyContent: "space-between", fontSize: "10px", color: tok.text.secondary, mb: 0.2 }}>
                    <span>{t("Carbon GWP")}</span>
                    <strong>{s.totalGwp.toLocaleString()} kg</strong>
                  </Box>
                  <LinearProgress 
                    variant="determinate" 
                    value={(s.totalGwp / maxSupplierGwp) * 100}
                    sx={{
                      height: 5,
                      borderRadius: 2.5,
                      bgcolor: isDark ? "#27272a" : "#e2e8f0",
                      "& .MuiLinearProgress-bar": {
                        bgcolor: isDark ? "#ef4444" : "#dc2626"
                      }
                    }}
                  />
                </Box>

                <Box>
                  <Box sx={{ display: "flex", justifyContent: "space-between", fontSize: "10px", color: tok.text.secondary, mb: 0.2 }}>
                    <span>{t("Circularity (MCI)")}</span>
                    <strong>{s.avgMci.toFixed(1)}%</strong>
                  </Box>
                  <LinearProgress 
                    variant="determinate" 
                    value={s.avgMci}
                    sx={{
                      height: 5,
                      borderRadius: 2.5,
                      bgcolor: isDark ? "#27272a" : "#e2e8f0",
                      "& .MuiLinearProgress-bar": {
                        bgcolor: isDark ? "#34d399" : "#16a34a"
                      }
                    }}
                  />
                </Box>
              </Box>
            ))}

            {supplierScorecard.length === 0 && (
              <Box sx={{ p: 4, textAlign: "center", width: "100%" }}>
                <Typography variant="body2" sx={{ color: tok.text.muted }}>{t("No supplier data found.")}</Typography>
              </Box>
            )}
          </Box>
        </Paper>
      </Box>

      {/* Sankey Diagram Card (Full-width) */}
      <Paper
        variant="Chart Container"
        sx={{
          p: ORG_PALETTE.spacing.chartPad,
          borderRadius: ORG_PALETTE.radius.card,
          border: "1px solid",
          borderColor: tok.border.card,
          bgcolor: tok.bg.card,
          overflow: "hidden",
          mb: 4
        }}
      >
        <Typography 
          variant="h6" 
          sx={{ 
            mb: 4, 
            pb: 2, 
            fontWeight: 500, 
            color: tok.text.chartTitle, 
            borderBottom: `1px solid ${tok.border.divider}`,
            textAlign: 'left'
          }}
        >
          {activeTab === "GWP" 
            ? t("Supplier Carbon Flow (GWP)") 
            : t("Supplier Circularity Flow (MCI)")
          }
        </Typography>
        
        <Box sx={{ overflowX: "auto", minWidth: "100%" }}>
          {sankeyData.links.length > 0 ? (
            <Box>
              <Box sx={{ display: "flex", justifyContent: "space-between", paddingX: 20, mb: 1 }}>
                <Typography sx={{ fontSize: "14px", fontWeight: 600, color: tok.text.secondary }}>{t("Supplier")}</Typography>
                <Typography sx={{ fontSize: "14px", fontWeight: 600, color: tok.text.secondary }}>{t("Product")}</Typography>
                <Typography sx={{ fontSize: "14px", fontWeight: 600, color: tok.text.secondary }}>
                  {activeTab === "GWP" ? t("Total GWP") : t("Circularity")}
                </Typography>
              </Box>
              <AdvancedSankey 
                data={sankeyData} 
                height={sankeyHeight} 
                nodeThickness={12} 
                nodeSpacing={26}
                margin={{ top: 20, right: 180, bottom: 20, left: 180 }}
                valueLabel={activeTab === "GWP" ? "kg CO₂e" : "%"}
              />
            </Box>
          ) : (
            <Box sx={{ p: 4, textAlign: "center" }}>
              <Typography sx={{ color: tok.text.muted }}>
                {t("No products match the selected filters. Try adjusting the sliders.")}
              </Typography>
            </Box>
          )}
        </Box>
      </Paper>

      {/* Supplier Performance Directory Table (ag-Grid) styled exactly like Product Dashboard table */}
      <Paper variant="Table Container" sx={{ mt: 4 }}>
        <TableHeaderContainer>
          <TableTitleContainer>
            <Typography variant="Table Title">
              {t("Supplier Performance Directory")}
            </Typography>
          </TableTitleContainer>
        </TableHeaderContainer>
        <Box sx={{ width: "100%" }}>
          <AgGrid
            columnDefs={gridColumnDefs}
            rowData={supplierScorecard}
            headerCheckbox={false}
            getRowId={(params) => params.data.name}
            domLayout={supplierScorecard.length <= 15 ? "autoHeight" : "normal"}
            maxHeight="858px"
          />
        </Box>
      </Paper>
    </MainContainer>
  );
};

export default ProductSupplierPortal;
